import React from "react";
import classNames from "classnames";
import { resolveLinkTarget } from "./resolveLinkTarget";
import "./OpticksLink.css";

export interface Props {
  to?: string /** Internal route — renders a react-router <Link> (SPA navigation). */;
  href?: string /** External/email destination — renders an <a>. A non-mailto href auto-opens a new tab (target/rel) + external icon; mailto adds the mail icon. */;
  label?: string;
  children?: React.ReactNode;
  variation?: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  className?: string;
  "aria-label"?: string;
}

/**
 * When to use OpticksLink:
 * - External link: just pass `href` — a non-mailto href auto-opens a new tab
 *   (`target="_blank"` + `rel`) and gets the trailing external-link icon; a
 *   `mailto:` href gets the mail icon. Only pass `target` to override (e.g. `_self`).
 * - Internal link: use the `to` prop only when it carries a leading `icon`. For a plain
 *   internal text link, skip OpticksLink and use react-router `<Link>` from the consumer
 *   (the default class is inert without an icon/disabled state, and link color/hover
 *   come from the global `a` rule either way).
 * - Anything beyond this layout: use a raw `<a>` / `<Link>` from the consumer.
 */
const OpticksLink = ({
  to,
  href,
  label,
  children,
  variation,
  target,
  rel,
  disabled = false,
  icon,
  onClick,
  className,
  "aria-label": ariaLabel,
}: Props) => {
  const classes = classNames(
    "opticks-link",
    variation && `variation-${variation}`,
    { "is-disabled": disabled },
    className
  );

  const { Element, targetProps, leadingIcon, trailingIcon } = resolveLinkTarget({
    to,
    href,
    target,
    rel,
    disabled,
  });

  return (
    <Element
      className={classes}
      {...targetProps}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {icon ?? leadingIcon}
      {children ?? label}
      {trailingIcon}
    </Element>
  );
};

export default OpticksLink;
